import { useQuery } from '@tanstack/react-query'
import { getDeadLetterStats, peekDeadLetters } from '../services/queuesApi'
import { FREQUENT } from '../utils/polling'

/**
 * Dead-letter queue depths (`GET api/system/dead-letters`).
 *
 * FREQUENT by default: a depth is a health counter, and it moves only when a
 * consumer gives up on a message or someone replays or purges by hand.
 *
 * `refetchInterval` is overridable because the Queues page and the rail badge
 * share this one cache entry at different rates: the badge passes
 * `{ refetchInterval: SLOW }`. Each observer polls on its own interval, and both
 * read the same payload, so the key carries no interval.
 */
export function useDeadLetterStats(options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: ['observatory', 'deadLetters'],
    queryFn: getDeadLetterStats,
    refetchInterval: options?.refetchInterval ?? FREQUENT,
  })
}

/**
 * Up to ten dead-lettered messages for one consumer queue, fetched only once one
 * is chosen. Pass a stats row's `sourceConsumer`, or null for "none chosen".
 *
 * Needs Lighthouse.Admin; the caller decides whether to ask (pass null when
 * `useLighthouseAdmin()` says denied) and renders the endpoint's own 403 as
 * restricted when the gate was unknown.
 *
 * No poll, and no refetch on window focus: somebody is reading these bodies,
 * and on RabbitMQ every peek gets and requeues each message. No retry either: a
 * 403, 401 or 400 will not change on a second try, and retrying one only delays
 * the state the reader should see.
 */
export function useDeadLetterPeek(queue: string | null) {
  return useQuery({
    queryKey: ['observatory', 'deadLetterPeek', queue],
    queryFn: () => peekDeadLetters(queue!),
    enabled: queue != null,
    refetchOnWindowFocus: false,
    retry: false,
  })
}
